/**
 * NumberField — an Inspector numeric row you can actually TYPE into.
 *
 * THE BUG THIS EXISTS FOR (2026-08-30, operator on iPad: "trying to type a
 * value in the start field of transpose item but it's not letting me").
 *
 * The Inspector's numeric rows were controlled inputs that wrote back to the
 * store on EVERY keystroke:
 *
 *     value={tr().start}
 *     onInput={(e) => setTransposeRange(tr().id, clamp(parseInt(e.currentTarget.value, 10) || 0), ...)}
 *
 * Two things make that unusable on a range that excludes the characters you
 * must type THROUGH:
 *
 *   1. `parseInt('')` and `parseInt('-')` are both NaN, and `|| 0` turns NaN
 *      into 0. Clearing the field, or typing the leading minus of "-5",
 *      immediately commits 0.
 *   2. Every `setXRange` rebuilds the item with a spread (`{ ...it, start: s }`),
 *      so the item identity changes on every write. Solid's `value={...}`
 *      effect therefore re-runs and rewrites the DOM value even when the
 *      NUMBER did not change - stomping the half-typed draft.
 *
 * For Transpose Start (min -12, max 0) that is fatal: the "-" commits 0 and
 * the DOM is rewritten to "0", then the "5" reads as +5, clamps back to 0, and
 * the DOM is rewritten again. Measured trace before the fix:
 *
 *     select-all -> "-5"    typed "-" -> DOM "0"    typed "5" -> DOM "0"
 *
 * It fails identically on desktop; it only got REPORTED on iPad because
 * desktop has arrow-key stepping and spinner buttons as an escape hatch and
 * iOS Safari has neither, so typing is the only route in.
 *
 * WHAT THIS COMPONENT DOES DIFFERENTLY
 *
 *   - Uncontrolled WHILE FOCUSED. The effect that mirrors `props.value` into
 *     the DOM skips entirely when the field has focus, so no store echo can
 *     stomp a draft. This is the load-bearing half - a controlled input
 *     cannot be made safe here while the store hands back fresh identities.
 *   - Intermediate drafts are left alone. "", "-", "-1" on the way to "-12",
 *     and out-of-range values are simply not committed; they are not coerced
 *     to 0 and not written back.
 *   - Commit on blur / Enter, clamped. An unparseable draft reverts to the
 *     stored value rather than snapping to 0.
 *   - Sign inference for negative-only ranges. When `max <= 0`, bare digits
 *     are read as their negative ("5" -> -5). A positive value is not in
 *     range, so there is nothing ambiguous to lose - and it means the field
 *     works on an iPad keyboard layer where the minus key is a layer away.
 *     Typing an explicit "-5" works exactly the same.
 *
 * Renders the same `<label class="inspector-field"><span/><input/></label>`
 * shape the Inspector CSS targets (`.inspector-field > input`), so it drops
 * into an `.inspector-zone` with no styling change.
 *
 * VERIFIED ON THE IPAD 2026-08-30 (operator, on the deployed build) - the
 * primary-device gate is closed for this component, not merely argued from a
 * green desktop suite. The specific risk that needed a real device: whether
 * `el.select()` in the focus handler actually fires in iOS Safari on a
 * `type="number"` input. It does. Had it not, tap-then-type would APPEND
 * rather than replace, so "-5" followed by "7" would read "-57" and clamp to
 * -12 on blur - a silently wrong value rather than a visible failure. Note
 * `setSelectionRange` is NOT an alternative here: it throws
 * InvalidStateError on number inputs. If this component is ever ported to
 * another input type, re-verify on the device rather than assuming.
 */

import { createEffect, createSignal } from 'solid-js';

interface NumberFieldProps {
  label: string;
  value: number;
  min: number;
  max: number;
  /** Called with a clamped integer, only when the value actually changes. */
  onCommit: (n: number) => void;
  title?: string;
}

export function NumberField(props: NumberFieldProps) {
  let el!: HTMLInputElement;
  const [focused, setFocused] = createSignal(false);

  const clamp = (n: number) => Math.max(props.min, Math.min(props.max, Math.round(n)));

  /** Parse a draft string, or null if it isn't a complete number yet.
   *  On a max<=0 range a bare positive magnitude means its negative. */
  function parseDraft(raw: string): number | null {
    const t = raw.trim();
    if (t === '' || t === '-' || t === '+') return null;
    const n = Number(t);
    if (!Number.isFinite(n)) return null;
    return props.max <= 0 && n > 0 ? -n : n;
  }

  function commit(n: number) {
    const c = clamp(n);
    if (c !== props.value) props.onCommit(c);
    return c;
  }

  // Mirror the store into the DOM only while NOT focused. Reading focused()
  // here is deliberate: blur flips it and this re-runs, which is what snaps
  // the field to the committed value.
  createEffect(() => {
    const v = props.value;
    if (!focused()) el.value = String(v);
  });

  return (
    <label class="inspector-field">
      <span>{props.label}</span>
      <input
        ref={el}
        type="number"
        min={props.min}
        max={props.max}
        title={props.title}
        onFocus={() => {
          setFocused(true);
          // Tap-and-type replaces rather than appends - on a touch surface
          // there is no cheap way to select the existing text by hand.
          el.select();
        }}
        onInput={() => {
          // Live-commit ONLY a complete, in-range value, so the canvas still
          // previews as you type. Anything mid-flight is left untouched.
          const n = parseDraft(el.value);
          if (n === null || n < props.min || n > props.max) return;
          if (n !== props.value) props.onCommit(n);
        }}
        onBlur={() => {
          const n = parseDraft(el.value);
          // Unparseable draft reverts to the stored value; it does NOT become 0.
          const settled = n === null ? props.value : commit(n);
          setFocused(false);
          el.value = String(settled);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); el.blur(); }
          if (e.key === 'Escape') { el.value = String(props.value); el.blur(); }
        }}
      />
    </label>
  );
}
